import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const OPEN_MAINTENANCE = new Set(['submitted', 'assigned', 'in_progress', 'pending', 'open']);

function daysUntil(endDate) {
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(String(endDate).includes('T') ? endDate : `${endDate}T00:00:00`);
  if (Number.isNaN(expiry.getTime())) return null;
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
}

/**
 * Live Home KPI counts after setup is complete.
 * Uses existing list APIs only — no sample/demo data.
 */
export default function useHomeKpis({ enabled, leases }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openMaintenance, setOpenMaintenance] = useState(0);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setOpenMaintenance(0);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/maintenance-requests');
      const rows = Array.isArray(res.data) ? res.data : [];
      const open = rows.filter((r) => {
        const status = String(r.status || '').toLowerCase();
        return OPEN_MAINTENANCE.has(status);
      }).length;
      setOpenMaintenance(open);
    } catch (err) {
      console.error('❌ Home KPI maintenance load failed:', err);
      setError(err.response?.data?.error || err.message || 'Could not load maintenance counts');
      setOpenMaintenance(0);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const leaseList = Array.isArray(leases) ? leases : [];
  const activeLeases = leaseList.filter((l) => {
    const days = daysUntil(l.endDate);
    if (days == null) return l.status === 'active' || !l.status;
    return days >= 0;
  }).length;

  const leasesEndingSoon = leaseList.filter((l) => {
    const days = daysUntil(l.endDate);
    return days != null && days >= 0 && days <= 60;
  }).length;

  return {
    loading,
    error,
    refresh,
    openMaintenance,
    activeLeases,
    leasesEndingSoon,
  };
}
